import { flushSync } from "react-dom";

/**
 * Applies a theme change inside a full-page View Transition, revealing the new
 * appearance from the switch's on-screen position.
 *
 * The Arc ThemeSwitch deliberately only reports the next theme; running the
 * page transition is the caller's job. This helper is that shared job:
 *   - falls back to an instant swap when the View Transitions API is missing,
 *     no trigger is given, or the user prefers reduced motion;
 *   - exposes the origin as --theme-switch-x / --theme-switch-y and the variant
 *     as data-theme-transition on <html>, which arc-bridge.css turns into the
 *     reveal / eclipse / split / rise keyframes;
 *   - uses flushSync so React has painted the new theme before the transition
 *     snapshots, otherwise both snapshots would show the old appearance.
 *
 * @param {"light"|"dark"} nextTheme
 * @param {HTMLElement} trigger  the ThemeSwitch button (from onThemeChange)
 * @param {(dark: boolean) => void} setDarkMode  the page's setter
 * @param {"reveal"|"eclipse"|"split"|"rise"} [variant]
 */
export function applyThemeWithTransition(
  nextTheme,
  trigger,
  setDarkMode,
  variant = "reveal",
) {
  const apply = () => flushSync(() => setDarkMode(nextTheme === "dark"));

  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const doc = typeof document !== "undefined" ? document : null;

  if (
    reduce ||
    !doc ||
    typeof doc.startViewTransition !== "function" ||
    !trigger
  ) {
    apply();
    return;
  }

  const { left, top, width, height } = trigger.getBoundingClientRect();
  const root = doc.documentElement;
  root.style.setProperty("--theme-switch-x", `${left + width / 2}px`);
  root.style.setProperty("--theme-switch-y", `${top + height / 2}px`);
  root.dataset.themeTransition = variant;

  const transition = doc.startViewTransition(apply);

  const cleanup = () => {
    root.style.removeProperty("--theme-switch-x");
    root.style.removeProperty("--theme-switch-y");
    delete root.dataset.themeTransition;
  };
  if (transition?.finished) transition.finished.finally(cleanup);
  else cleanup();
}
